"use client";

import { CheckIcon } from "@cmp-src/icons/icons";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import { Button } from "@cmp-src/primitives/button";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AiChatToolShowVisitInfoOutput,
  AiChatToolShowVisitInfoPart
} from "@gettactic/api";
import {
  type OfficeWithStations,
  VisitorTypeField
} from "@gettactic/api/src/endpoints/visitor-manager";
import { getApiErrorMessage } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, isValid, parse } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import {
  UseTessaMessageStateReturn,
  useTessaMessageState
} from "@/features/tessa/components/utils/useTessaMessageState";
import { TessaMessageRendererProps } from "@/features/tessa/types";
import {
  buildVisitCreatePayload,
  PRE_REGISTER_DEFAULT_VALUES,
  type PreRegisterFormValues,
  preRegisterSchema,
  VisitorPreRegistrationFormFields
} from "@/features/visitors/components/VisitorPreRegistrationFormFields";
import { api } from "@/lib/api/api";

type InvitedVisit = { visitor_name: string; code?: string };

// The invitation Tessa prepared, in the form's shape; the expected time is the office's wall clock
export const visitInviteDefaultValues = (
  output: AiChatToolShowVisitInfoOutput
): PreRegisterFormValues => {
  const expected = output.expected
    ? parse(output.expected.slice(0, 16), "yyyy-MM-dd'T'HH:mm", new Date())
    : null;
  const hasExpected = !!expected && isValid(expected);

  return {
    ...PRE_REGISTER_DEFAULT_VALUES,
    office_id: output.office_id ?? "",
    visitor_type_id: output.visitor_type_id ?? "",
    visitor_name: output.visitor_name ?? "",
    visitor_email: output.visitor_email ?? "",
    expected_date: hasExpected
      ? expected
      : PRE_REGISTER_DEFAULT_VALUES.expected_date,
    expected_time: hasExpected ? format(expected, "hh:mm a") : ""
  };
};

// The form's date and time are the office's wall clock, so they're sent as that time in the office
export const officeExpectedTime = (
  data: Pick<PreRegisterFormValues, "expected_date" | "expected_time">,
  timeZone: string
): string => {
  const time = parse(data.expected_time, "hh:mm a", new Date());
  const wallClock = `${format(data.expected_date, "yyyy-MM-dd")}T${format(time, "HH:mm")}:00`;
  return fromZonedTime(wallClock, timeZone).toISOString();
};

// The office's time zone from the visitor station offices the picker lists, else the user's own offices.
// Undefined when neither knows the office, so the visit isn't sent on the browser's clock.
export const officeTimeZone = (
  officeId: string,
  stationOffices: OfficeWithStations[] | undefined,
  userOffices: { id: string; time_zone?: string }[] | undefined
): string | undefined =>
  stationOffices?.find(({ office }) => office.id === officeId)?.office
    .time_zone ||
  userOffices?.find((office) => office.id === officeId)?.time_zone ||
  undefined;

const COMPANY_FIELD = /company|organi[sz]ation/i;

const VisitInviteForm: React.FC<{
  output: AiChatToolShowVisitInfoOutput;
  onInvited: (visit: InvitedVisit) => void;
}> = ({ output, onInvited }) => {
  const queryClient = useQueryClient();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const customFieldsRef = useRef<VisitorTypeField[]>([]);
  const form = useForm<PreRegisterFormValues>({
    resolver: zodResolver(preRegisterSchema),
    defaultValues: visitInviteDefaultValues(output)
  });
  // Subscribe to formState.errors so child inputs re-render on validation failure
  const { errors: _formErrors } = form.formState;

  const officeId = form.watch("office_id");
  const visitorTypeId = form.watch("visitor_type_id");

  // Same query as the form fields' office picker, so it's only loaded once
  const { data: stationOffices } = useQuery({
    queryKey: ["visitorStations.listOffices"],
    queryFn: async () => {
      const response = await api.client.visitorStations.listOffices();
      return response.result;
    }
  });

  // Same query as the form fields, so the types are only loaded once
  const { data: visitorTypes } = useQuery({
    queryKey: ["visitorTypes.list", { office_id: officeId || null }],
    queryFn: async () => {
      const response =
        await api.client.visitorStations.listOfficeTypes(officeId);
      return response.result;
    },
    enabled: !!officeId
  });

  // The company Tessa heard goes into the visitor type's company field, if it has one
  useEffect(() => {
    if (!output.company) return;
    const companyField = visitorTypes
      ?.find((visitorType) => visitorType.id === visitorTypeId)
      ?.fields.find(
        (field) =>
          (field.field_type === "text" || field.field_type === "textarea") &&
          COMPANY_FIELD.test(field.name)
      );
    // biome-ignore lint/suspicious/noExplicitAny: custom fields are named by their ID
    const values = form.getValues() as any;
    if (companyField && !values[companyField.id]) {
      // biome-ignore lint/suspicious/noExplicitAny: custom fields are named by their ID
      form.setValue(companyField.id as any, output.company);
    }
  }, [visitorTypes, visitorTypeId, output.company, form]);

  // Read synchronously, so a second submit before the re-render doesn't invite twice; released only if the
  // invite fails, since a sent one replaces the form
  const inviting = useRef(false);
  const invite = useMutation({
    mutationFn: async (data: PreRegisterFormValues) => {
      const payload = buildVisitCreatePayload(data, customFieldsRef.current);
      const timeZone = officeTimeZone(
        data.office_id,
        stationOffices ?? undefined,
        authenticatedUser.offices?.offices
      );
      if (!timeZone) {
        throw new Error(
          "The office's time zone isn't loaded yet. Try again in a moment."
        );
      }
      payload.expected = officeExpectedTime(data, timeZone);
      const response = await api.client.visits.create(payload);
      return { visitor_name: data.visitor_name, code: response.result?.code };
    },
    onSuccess: (visit) => {
      queryClient.invalidateQueries({ queryKey: ["visits.list"] });
      onInvited(visit);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "The visitor couldn't be invited"));
    }
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((data) => {
        if (inviting.current) return;
        inviting.current = true;
        invite.mutate(data, {
          onError: () => {
            inviting.current = false;
          }
        });
      })}
    >
      <VisitorPreRegistrationFormFields
        form={form}
        isActive
        customFieldsRef={customFieldsRef}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={invite.isPending}>
          Invite visitor
        </Button>
      </div>
    </form>
  );
};

export const VisitInvited: React.FC<{ visit: InvitedVisit }> = ({ visit }) => (
  <div className="flex items-start gap-3">
    <CheckIcon className="size-8 shrink-0 text-green-500" />
    <div className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-neutral-900">
        {visit.visitor_name} is invited
      </span>
      {visit.code ? (
        <span className="text-neutral-600">
          They'll get an email with their check-in code{" "}
          <span className="font-mono font-semibold text-neutral-900 tracking-widest">
            {visit.code}
          </span>
        </span>
      ) : null}
    </div>
  </div>
);

export const ChatBotToolShowVisitInfo: React.FC<
  TessaMessageRendererProps<AiChatToolShowVisitInfoPart>
> = ({ part, message }) => {
  const askTessa = useAskTessa();
  const messageState: UseTessaMessageStateReturn | null = useTessaMessageState(
    askTessa?.selectedChatId,
    message,
    part
  );
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowVisitInfoOutput
    | undefined;

  // Shown as soon as the invite is sent, without waiting for the message state to save
  const [sentVisit, setSentVisit] = useState<InvitedVisit>();

  if (!output) return <TacticLoader className="opacity-60" />;

  const invited =
    sentVisit ??
    (messageState?.state === "completed"
      ? (messageState.data?.data as InvitedVisit | undefined)
      : undefined);

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      {invited ? (
        <VisitInvited visit={invited} />
      ) : (
        <VisitInviteForm
          output={output}
          onInvited={(visit) => {
            setSentVisit(visit);
            messageState?.setMessageState("completed", { data: visit });
          }}
        />
      )}
    </ChatBotToolCard>
  );
};
